import React, { useState } from 'react';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Tooltip,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, MicrosoftIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useClientSpla } from '../../../hooks/useClientSpla';
import { formatUnitPrice, trimDecimalZeros } from '../../../utils/money';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { SplaSubjectCell } from '../../utils/splaSubjects';
import type { ClientSplaLine } from '../../../types/models/Spla';

/**
 * Microsoft SPLA (Services Provider License Agreement) — the organization's CURRENT license
 * consumption and the sale price it pays for it, one line per consumed license with the consuming
 * subjects as an expandable drill-down.
 *
 * Everything shown comes from `GET /clients/spla` verbatim: the per-line and grand totals are
 * computed by the backend (never client-side), and the payload deliberately carries no distributor
 * price, staff note, or provenance — an unpriced line renders as a calm "Price on request", never
 * an error state.
 */
function MicrosoftSplaTab() {
  const { spla, loading, error, refetch } = useClientSpla();

  // Expanded lines tracked by license id (stable across refetches, unlike row order).
  const [expandedLicenseIds, setExpandedLicenseIds] = useState<number[]>([]);
  const isExpanded = (line: ClientSplaLine) => expandedLicenseIds.includes(line.license.id);
  const setExpanded = (line: ClientSplaLine, expanding: boolean) =>
    setExpandedLicenseIds((prev) => {
      const others = prev.filter((id) => id !== line.license.id);
      return expanding ? [...others, line.license.id] : others;
    });

  if (loading && spla === null) {
    return <CenteredSpinner />;
  }

  if (error !== null && spla === null) {
    return (
      <EmptyState
        titleText="Unable to load your SPLA consumption"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (spla === null || spla.lines.length === 0) {
    return (
      <EmptyState titleText="No SPLA consumption" icon={MicrosoftIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>No SPLA consumption recorded for your organization.</EmptyStateBody>
      </EmptyState>
    );
  }

  const renderUnitPrice = (line: ClientSplaLine) => {
    if (!line.priced || line.unit_price === null) {
      return (
        <Tooltip content="This license has not been priced for your organization yet — you will not be billed for it until it is.">
          <Content component="small">Price on request</Content>
        </Tooltip>
      );
    }
    return (
      <>
        {formatUnitPrice(line.unit_price)}
        {line.is_custom === true && (
          <>
            {' '}
            <Tooltip content="A price specific to your organization applies.">
              <Label color="blue" isCompact>
                custom price
              </Label>
            </Tooltip>
          </>
        )}
      </>
    );
  };

  const columnCount = 6;

  return (
    <>
      <Content component="p" className="pf-v6-u-mb-md">
        Your organization&apos;s current Microsoft SPLA license consumption, reported monthly. Prices are
        per unit and per month, EUR excl. VAT. Expand a line to see which machines consume it.
      </Content>
      <Table aria-label="Microsoft SPLA license consumption" variant="compact" isExpandable>
        <Thead>
          <Tr>
            <Th screenReaderText="Row expansion" />
            <Th>License</Th>
            <Th>Quantity</Th>
            <Th>Unit price</Th>
            <Th>Monthly total</Th>
            <Th>Updated</Th>
          </Tr>
        </Thead>
        {spla.lines.map((line, rowIndex) => (
          <Tbody key={line.license.id} isExpanded={isExpanded(line)}>
            <Tr isContentExpanded={isExpanded(line)}>
              <Td
                expand={
                  line.subjects.length > 0
                    ? {
                        rowIndex,
                        isExpanded: isExpanded(line),
                        onToggle: () => setExpanded(line, !isExpanded(line)),
                        expandId: 'spla-line-subjects',
                      }
                    : undefined
                }
              />
              <Td dataLabel="License">
                {line.license.product_family}
                <div>
                  <Content component="small">
                    {line.license.part_number}
                    {line.license.category_name !== null ? ` · ${line.license.category_name}` : ''}
                    {line.license.agreement_type === 'Academic' ? ` · Academic ${line.license.offering}` : ''}
                  </Content>
                </div>
              </Td>
              <Td dataLabel="Quantity">{trimDecimalZeros(line.quantity)}</Td>
              <Td dataLabel="Unit price">{renderUnitPrice(line)}</Td>
              <Td dataLabel="Monthly total">
                {line.monthly_total !== null ? formatUnitPrice(line.monthly_total) : '—'}
              </Td>
              <Td dataLabel="Updated">{formatWallClockDateTime(line.updated_at)}</Td>
            </Tr>
            {line.subjects.length > 0 && (
              <Tr isExpanded={isExpanded(line)}>
                <Td />
                <Td dataLabel="Consuming subjects" colSpan={columnCount - 1}>
                  <ExpandableRowContent>
                    <Table
                      aria-label={`Subjects consuming ${line.license.product_family}`}
                      variant="compact"
                      borders={false}
                    >
                      <Thead>
                        <Tr>
                          <Th>Subject</Th>
                          <Th>Quantity</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {line.subjects.map((subject) => (
                          <Tr key={`${subject.type}-${subject.ref}`}>
                            <Td dataLabel="Subject">
                              <SplaSubjectCell type={subject.type} subjectRef={subject.ref} name={subject.name} />
                            </Td>
                            <Td dataLabel="Quantity">{trimDecimalZeros(subject.quantity)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </ExpandableRowContent>
                </Td>
              </Tr>
            )}
          </Tbody>
        ))}
        <Tbody>
          <Tr>
            <Td colSpan={4}>
              <strong>Monthly total (priced lines)</strong>
            </Td>
            <Td dataLabel="Monthly total">
              <strong>{formatUnitPrice(spla.total)}</strong>
            </Td>
            <Td />
          </Tr>
        </Tbody>
      </Table>
      {spla.unpriced_count > 0 && (
        <Content component="p" className="pf-v6-u-mt-sm">
          <Content component="small">
            {spla.unpriced_count} line{spla.unpriced_count === 1 ? '' : 's'} not yet priced — not included
            in the total.
          </Content>
        </Content>
      )}
    </>
  );
}

export default MicrosoftSplaTab;
